/**
 * Holds site-wide user roles. If a user is not assigned any global roles, then it is a read-only user
 * who can only view publicly available information without access to any billed APIs or functionality.
 */
export enum GlobalRole {
  /**
   * Admin has full access to all features of the site.
   */
  ADMIN = "ADMIN",
  /**
   * Customer is allowed to create companies, call documents generation and other functionality.
   */
  CUSTOMER = "CUSTOMER"
}

/**
 * Holds all roles that users can have within a specific company.
 */
export enum CompanyRole {
  /**
   * Owner of a company has access to all Company-related functionality:
   * can create documents, add and manage users, etc.
   */
  OWNER = "OWNER",
  /**
   * Viewer has read-only access to company's artifacts, e.g. can't create/delete documents, start chats.
   */
  VIEWER = "VIEWER",
  /**
   * Editor can view and edit company's artifacts, initiate chats. Can't manage users and their roles.
   */
  EDITOR = "EDITOR",
  /**
   * Engineering role for engineering team members.
   */
  ENGINEERING = "ENGINEERING",
  /**
   * Product role for product team members.
   */
  PRODUCT = "PRODUCT",
  /**
   * Marketing role for marketing team members.
   */
  MARKETING = "MARKETING",
  /**
   * Allows managing of projects and roadmaps.
   */
  PROJECTS_OWNER = "PROJECTS_OWNER",
}

export function getCompanyRoleName(role: CompanyRole) {
  switch (role) {
    case CompanyRole.OWNER: return "Admin"
    case CompanyRole.EDITOR: return "User"
    case CompanyRole.VIEWER: return "Viewer"
    case CompanyRole.ENGINEERING: return "Engineering"
    case CompanyRole.PRODUCT: return "Product"
    case CompanyRole.MARKETING: return "Marketing"
    case CompanyRole.PROJECTS_OWNER: return "Projects owner"
    default: return role
  }
}

export const PRIMARY_COMPANY_ROLES: readonly CompanyRole[] = [CompanyRole.OWNER, CompanyRole.EDITOR];

export const LEGACY_COMPANY_ROLES: readonly CompanyRole[] = [
  CompanyRole.VIEWER,
  CompanyRole.ENGINEERING,
  CompanyRole.PRODUCT,
  CompanyRole.MARKETING,
  CompanyRole.PROJECTS_OWNER,
];

export function getAllCompanyRoles(): CompanyRole[] {
  return Object.values(CompanyRole);
}

export function isLegacyCompanyRole(role: CompanyRole): boolean {
  return LEGACY_COMPANY_ROLES.includes(role);
}

export function getSelectableCompanyRoles(options: {
  includeLegacy: boolean;
  currentRoles?: CompanyRole[];
}): CompanyRole[] {
  const base = options.includeLegacy ? getAllCompanyRoles() : [...PRIMARY_COMPANY_ROLES];
  if (!options.currentRoles?.length) {
    return base;
  }
  const extras = options.currentRoles.filter(role => !base.includes(role));
  return [...base, ...extras];
}